import React, { useEffect, useMemo, useState } from 'react';
import { PUBLIC_APPLICATIONS } from '../../data/publicApplications';

const resolveAppBase = () => {
  if (typeof window === 'undefined') return '/';
  const explicitBase = process.env.PUBLIC_URL || '';
  let base = explicitBase;
  if (!base || base === '/') {
    const path = window.location.pathname || '/';
    base = path === '/app' || path.startsWith('/app/') ? '/app' : '/';
  }
  return base.endsWith('/') ? base.slice(0, -1) : base;
};

const normalizeUrl = (url) => {
  const value = String(url || '').trim();
  if (!value) return '';
  if (value.startsWith('/apps/') && /^https?:\/\//i.test(process.env.REACT_APP_API_URL || '')) {
    try {
      return `${new URL(process.env.REACT_APP_API_URL).origin}${value}`;
    } catch {
      return value;
    }
  }
  if (value.startsWith('/')) return value;
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
};

const isExternalUrl = (url) => /^https?:\/\//i.test(String(url || ''));

const resolveApiBase = () => {
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  return apiBase || '/api';
};

const resolveHref = (href, appBase) => {
  if (!href) return '#';
  if (/^(https?:)?\/\//i.test(href) || href.startsWith('#')) return href;
  if (href.startsWith('/')) return href;
  return `${appBase || ''}/${href}`.replace(/\/{2,}/g, '/');
};

const renderLayoutSection = (section, appBase) => {
  const paddingTop = Number(section?.padding?.top ?? 70);
  const paddingBottom = Number(section?.padding?.bottom ?? 70);
  const gap = Number(section?.gap ?? 24);
  const bgType = section?.background?.type || 'color';
  const bgValue = section?.background?.value || '#ffffff';
  const overlay = section?.background?.overlay || '';
  const columns = Array.isArray(section?.columns) ? section.columns : [];

  return (
    <section
      key={section?.id || section?.name}
      className="pub-apps-layout-section"
      style={{
        paddingTop,
        paddingBottom,
        background: bgType === 'color' ? bgValue : 'transparent',
      }}
    >
      {bgType === 'image' && bgValue && (
        <div className="pub-apps-bg" style={{ backgroundImage: `url('${bgValue}')` }} />
      )}
      {overlay ? <div className="pub-apps-overlay" style={{ background: overlay }} /> : null}
      <div className="pub-apps-layout-inner">
        <div className="pub-apps-layout-grid" style={{ gap }}>
          {columns.map((column, columnIndex) => {
            const width = Math.max(1, Math.min(12, Number(column?.width || 12)));
            const align = column?.align || 'left';
            const items = Array.isArray(column?.items) ? column.items : [];

            return (
              <div
                key={column?.id || columnIndex}
                className="pub-apps-layout-col"
                data-align={align}
                style={{ gridColumn: `span ${width}` }}
              >
                {items.map((item, itemIndex) => {
                  if ((item?.type || 'text') === 'text') {
                    const styles = item?.styles || {};
                    const lines = String(item?.content || '').split('\n');
                    return (
                      <p
                        key={item?.id || itemIndex}
                        style={{
                          margin: 0,
                          color: styles?.color || '#111111',
                          fontSize: Number(styles?.font_size || 18),
                          fontWeight: Number(styles?.font_weight || 400),
                          fontStyle: styles?.font_style || 'normal',
                          lineHeight: Number(styles?.line_height || 1.5),
                        }}
                      >
                        {lines.map((line, lineIndex) => (
                          <React.Fragment key={lineIndex}>
                            {line}
                            {lineIndex < lines.length - 1 ? <br /> : null}
                          </React.Fragment>
                        ))}
                      </p>
                    );
                  }

                  if (item?.type === 'image') {
                    const styles = item?.styles || {};
                    if (!item?.src) return null;
                    return (
                      <img
                        key={item?.id || itemIndex}
                        src={item.src}
                        alt={item?.alt || 'Image'}
                        loading="lazy"
                        style={{
                          width: `${Number(styles?.width || 100)}%`,
                          objectFit: styles?.fit || 'cover',
                          borderRadius: Number(styles?.border_radius || 0),
                        }}
                      />
                    );
                  }

                  if (item?.type === 'button') {
                    const styles = item?.styles || {};
                    const isOutline = String(styles?.variant || 'solid') === 'outline';
                    const bg = styles?.background_color || '#1e3a8a';
                    return (
                      <a
                        key={item?.id || itemIndex}
                        className="pub-apps-layout-button"
                        href={resolveHref(item?.url || '#', appBase)}
                        style={{
                          background: isOutline ? 'transparent' : bg,
                          color: styles?.text_color || '#ffffff',
                          borderColor: bg,
                          fontSize: Number(styles?.font_size || 16),
                          fontWeight: Number(styles?.font_weight || 600),
                          borderRadius: Number(styles?.border_radius || 10),
                          padding: `${Number(styles?.padding_y || 12)}px ${Number(styles?.padding_x || 20)}px`,
                        }}
                      >
                        {item?.text || 'Button'}
                      </a>
                    );
                  }

                  return null;
                })}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const PublicApplications = () => {
  const appBase = useMemo(() => resolveAppBase(), []);
  const [loadingFlags, setLoadingFlags] = useState(true);
  const [loadingLayout, setLoadingLayout] = useState(true);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const [flagsError, setFlagsError] = useState('');
  const [layoutError, setLayoutError] = useState('');
  const [applicationsError, setApplicationsError] = useState('');
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [layout, setLayout] = useState(null);
  const [applicationCatalog, setApplicationCatalog] = useState(PUBLIC_APPLICATIONS);

  const homeHref = appBase ? `${appBase}/` : '/';
  const tvHref = `${appBase || ''}/tv` || '/tv';
  const aboutHref = `${appBase || ''}/about-us` || '/about-us';
  const servicesHref = `${appBase || ''}/services` || '/services';
  const applicationsHref = `${appBase || ''}/applications` || '/applications';
  const heraldHref = '/herald';
  const radioHref = process.env.REACT_APP_PUBLIC_RADIO_URL || 'https://heavensembassy.radio12345.com';
  const loginHref = `${appBase || ''}/login` || '/login';

  const apps = useMemo(
    () => applicationCatalog.map((app, index) => ({ ...app, index: index + 1, url: normalizeUrl(app.url) })),
    [applicationCatalog]
  );

  const liveCount = apps.filter((app) => app.url).length;
  const pendingCount = apps.length - liveCount;
  const maxWidth = Number(layout?.settings?.max_width || 1200);
  const pageTitle = layout?.settings?.page_title || 'Kingdom Vision International Network';
  const backgroundColor = layout?.settings?.background_color || '#f8f4ea';
  const textColor = layout?.settings?.text_color || '#1f1f1f';

  useEffect(() => {
    let mounted = true;

    const loadFlags = async () => {
      setLoadingFlags(true);
      try {
        const response = await fetch(`${resolveApiBase()}/public/site-flags`, {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load site status.');
        }
        if (!mounted) return;
        setMaintenanceEnabled(Boolean(data?.applications_maintenance_enabled));
        setFlagsError('');
      } catch (error) {
        if (!mounted) return;
        setFlagsError(error.message || 'Unable to load site status.');
        setMaintenanceEnabled(false);
      } finally {
        if (mounted) setLoadingFlags(false);
      }
    };

    loadFlags();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadApplications = async () => {
      setLoadingApplications(true);
      try {
        const response = await fetch(`${resolveApiBase()}/public/applications`, {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load applications.');
        }
        if (!mounted) return;
        setApplicationCatalog(Array.isArray(data?.applications) ? data.applications : PUBLIC_APPLICATIONS);
        setApplicationsError('');
      } catch (error) {
        if (!mounted) return;
        setApplicationsError(error.message || 'Unable to load applications.');
        setApplicationCatalog(PUBLIC_APPLICATIONS);
      } finally {
        if (mounted) setLoadingApplications(false);
      }
    };

    loadApplications();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadLayout = async () => {
      setLoadingLayout(true);
      try {
        const response = await fetch(`${resolveApiBase()}/public/homepage?key=applications`, {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load applications layout.');
        }
        if (!mounted) return;
        setLayout(data.layout || null);
        setLayoutError('');
      } catch (error) {
        if (!mounted) return;
        setLayoutError(error.message || 'Unable to load applications layout.');
      } finally {
        if (mounted) setLoadingLayout(false);
      }
    };

    loadLayout();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <main className="pub-apps-page" style={{ background: backgroundColor, color: textColor }}>
      <style>
        {`
          .pub-apps-page { min-height: 100vh; }
          .pub-apps-topbar { position: sticky; top: 0; z-index: 20; backdrop-filter: blur(8px); background: rgba(255,255,255,0.92); border-bottom: 1px solid rgba(0,0,0,0.08); }
          .pub-apps-topbar-inner { width: min(100% - 2rem, ${maxWidth}px); margin: 0 auto; min-height: 68px; display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
          .pub-apps-brand { font-weight: 700; font-size: clamp(1rem, 1.5vw, 1.2rem); text-decoration: none; color: #1f1f1f; }
          .pub-apps-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
          .pub-apps-link { display: inline-flex; align-items: center; justify-content: center; text-decoration: none; font-weight: 600; font-size: 0.92rem; border-radius: 999px; padding: 0.62rem 0.95rem; border: 1px solid transparent; color: #201600; }
          .pub-apps-link:hover { background: rgba(32,22,0,0.07); border-color: rgba(32,22,0,0.12); }
          .pub-apps-link.active { background: #201600; border-color: #201600; color: #fff7e7; }
          .pub-apps-login { display: inline-flex; align-items: center; justify-content: center; text-decoration: none; font-weight: 700; font-size: 0.95rem; border-radius: 999px; padding: 0.65rem 1.05rem; border: 1px solid #201600; color: #201600; }
          .pub-apps-login:hover { background: #201600; color: #fff7e7; }

          .pub-apps-layout-section { position: relative; overflow: hidden; }
          .pub-apps-bg { position: absolute; inset: 0; background-position: center; background-repeat: no-repeat; background-size: cover; z-index: 0; }
          .pub-apps-overlay { position: absolute; inset: 0; z-index: 1; }
          .pub-apps-layout-inner { position: relative; z-index: 2; width: min(100% - 2rem, ${maxWidth}px); margin: 0 auto; }
          .pub-apps-layout-grid { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); }
          .pub-apps-layout-col { display: flex; flex-direction: column; gap: 0.9rem; min-width: 0; }
          .pub-apps-layout-col[data-align="center"] { text-align: center; align-items: center; }
          .pub-apps-layout-col[data-align="right"] { text-align: right; align-items: flex-end; }
          .pub-apps-layout-button { display: inline-block; text-decoration: none; border: 1px solid transparent; }
          .pub-apps-wrap { width: min(100% - 2rem, ${maxWidth}px); margin: 0 auto; padding: 2rem 0 3rem; display: grid; gap: 1.2rem; }
          .pub-apps-metrics { margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 0.55rem; }
          .pub-apps-pill { display: inline-flex; align-items: center; padding: 0.38rem 0.78rem; border-radius: 999px; border: 1px solid #e7d9c1; background: #fff; font-size: 0.84rem; font-weight: 700; color: #594217; }

          .pub-apps-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 0.85rem; }
          .pub-apps-card { border: 1px solid #eee3d2; background: #fff; border-radius: 16px; padding: 1rem; display: grid; gap: 0.65rem; box-shadow: 0 8px 24px rgba(37, 28, 8, 0.05); }
          .pub-apps-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.7rem; }
          .pub-apps-card h2 { margin: 0; font-size: 1.03rem; color: #2a1d08; line-height: 1.4; }
          .pub-apps-card-index { border-radius: 999px; border: 1px solid #e7d9c1; background: #fff9ef; color: #6a4f20; font-weight: 700; font-size: 0.78rem; line-height: 1; padding: 0.33rem 0.5rem; white-space: nowrap; }
          .pub-apps-url { margin: 0; color: #7b6440; font-size: 0.84rem; word-break: break-word; }
          .pub-apps-note { margin: 0; color: #856523; font-size: 0.84rem; font-weight: 600; }
          .pub-apps-open { width: max-content; text-decoration: none; border-radius: 999px; border: 1px solid #2a1d08; color: #2a1d08; font-weight: 700; font-size: 0.85rem; padding: 0.48rem 0.86rem; }
          .pub-apps-open:hover { background: #2a1d08; color: #fff7e7; }
          .pub-apps-pending { width: max-content; border-radius: 999px; border: 1px solid #e2c88f; color: #8a641f; background: #fff8e7; font-size: 0.8rem; font-weight: 700; padding: 0.35rem 0.7rem; }

          @media (max-width: 760px) {
            .pub-apps-topbar-inner { min-height: auto; padding: 0.6rem 0; flex-direction: column; align-items: flex-start; }
            .pub-apps-layout-grid { grid-template-columns: 1fr; }
            .pub-apps-layout-col { grid-column: 1 / -1 !important; }
          }
        `}
      </style>

      <header className="pub-apps-topbar">
        <div className="pub-apps-topbar-inner">
          <a className="pub-apps-brand" href={homeHref}>
            {pageTitle}
          </a>
          <div className="pub-apps-actions">
            <a className="pub-apps-link" href={homeHref}>
              Home
            </a>
            <a className="pub-apps-link" href={tvHref}>
              TV
            </a>
            <a className="pub-apps-link" href={radioHref}>
              Radio
            </a>
            <a className="pub-apps-link" href={aboutHref}>
              About Us
            </a>
            <a className="pub-apps-link" href={servicesHref}>
              Services
            </a>
            {!loadingFlags && !maintenanceEnabled && (
              <a className="pub-apps-link active" href={applicationsHref}>
                Applications
              </a>
            )}
            <a className="pub-apps-link" href={heraldHref}>
              Herald
            </a>
            <a className="pub-apps-login" href={loginHref}>
              Login
            </a>
          </div>
        </div>
      </header>

      {loadingLayout && (
        <div className="pub-apps-wrap">
          <article className="pub-apps-card">
            <h2>Loading applications layout...</h2>
          </article>
        </div>
      )}
      {!loadingLayout && layoutError && (
        <div className="pub-apps-wrap">
          <article className="pub-apps-card">
            <h2>Could not load page layout</h2>
            <p className="pub-apps-note">{layoutError}</p>
          </article>
        </div>
      )}
      {!loadingLayout &&
        !layoutError &&
        Array.isArray(layout?.sections) &&
        layout.sections.map((section) => renderLayoutSection(section, appBase))}

      <section className="pub-apps-wrap">
        <article className="pub-apps-card">
          <h2>Application Directory</h2>
          <div className="pub-apps-metrics">
            <span className="pub-apps-pill">Total apps: {apps.length}</span>
            <span className="pub-apps-pill">Live links: {liveCount}</span>
            <span className="pub-apps-pill">Pending links: {pendingCount}</span>
          </div>
        </article>

        {(loadingFlags || loadingApplications) && (
          <article className="pub-apps-card">
            <h2>Loading applications...</h2>
          </article>
        )}

        {!loadingFlags && flagsError && (
          <article className="pub-apps-card">
            <h2>Could not verify maintenance status</h2>
            <p className="pub-apps-note">{flagsError}</p>
          </article>
        )}

        {!loadingApplications && applicationsError && (
          <article className="pub-apps-card">
            <h2>Could not load editable application catalog</h2>
            <p className="pub-apps-note">{applicationsError}</p>
          </article>
        )}

        {!loadingFlags && maintenanceEnabled && (
          <article className="pub-apps-card">
            <h2>Applications are temporarily unavailable</h2>
            <p className="pub-apps-note">
              This page is currently in maintenance mode. Please check back later.
            </p>
          </article>
        )}

        {!loadingFlags && !loadingApplications && !maintenanceEnabled && (
          <div className="pub-apps-grid">
            {apps.map((app) => {
              const external = isExternalUrl(app.url);
              const displayUrl = app.url.replace(/^https?:\/\//i, '');

              return (
                <article key={app.name} className="pub-apps-card">
                  <div className="pub-apps-card-head">
                    <h2>{app.name}</h2>
                    <span className="pub-apps-card-index">#{app.index}</span>
                  </div>

                  {app.url ? <p className="pub-apps-url">{displayUrl}</p> : null}
                  {app.note ? <p className="pub-apps-note">{app.note}</p> : null}

                  {app.url ? (
                    <a
                      className="pub-apps-open"
                      href={app.url}
                      target={external ? '_blank' : undefined}
                      rel={external ? 'noopener noreferrer' : undefined}
                    >
                      Open App
                    </a>
                  ) : (
                    <span className="pub-apps-pending">Link pending</span>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
};

export default PublicApplications;
